import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@shared/api';
import { Sparkline } from '@shared/charts';
import { idr, num, relative } from '@shared/format';
import { EnvBadge, type ApiKeyRow } from '../components/ApiKeyBlocks';
import { ModelAccessSummary, useCatalogModels } from '../components/ModelAccess';
import {
  BudgetBlock,
  DAILY_TIP,
  FALLBACK_TIP,
  FallbackChain,
  MONTHLY_TIP,
  ProjectModal,
  loadWeekSpend,
  type Project,
} from '../components/ProjectBlocks';
import { Alert, Badge, Card, Empty, Icon, InfoTip, Loading, PageHeader } from '../components/ui';
import type { CatalogModel } from '../catalog';
import '../styles/billing.css';

const STATUS_LABELS: Partial<Record<string, string>> = {
  ACTIVE: 'Aktif',
  DISABLED: 'Nonaktif',
};

export function Projects() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [keys, setKeys] = useState<ApiKeyRow[] | null>(null);
  const [week, setWeek] = useState<Record<string, number[]> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Project | 'new' | null>(null);
  const catalog = useCatalogModels();
  const bySlug = useMemo(() => new Map((catalog ?? []).map((m) => [m.id, m])), [catalog]);

  const load = async () => {
    try {
      const [p, k, w] = await Promise.all([
        api<{ data: Project[] }>('/v1/projects'),
        api<{ data: ApiKeyRow[] }>('/v1/api-keys').catch(() => null),
        loadWeekSpend(),
      ]);
      setProjects(p.data);
      setKeys(k ? k.data : null);
      setWeek(w);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  if (loading) return <Loading label="Memuat proyek" />;

  return (
    <>
      <PageHeader
        title="Proyek"
        subtitle="Proyek mengelompokkan API key. Batas biaya dan model cadangan di sini berlaku untuk semua key di dalamnya."
        actions={
          <button className="btn btn-primary" onClick={() => setEditing('new')}>
            <Icon name="plus" size={16} />
            Proyek baru
          </button>
        }
      />

      {error && <Alert kind="error">{error}</Alert>}

      {projects.length === 0 ? (
        <Card>
          <Empty
            icon="folder"
            action={
              <button className="btn btn-primary" onClick={() => setEditing('new')}>
                <Icon name="plus" size={16} />
                Buat proyek
              </button>
            }
          >
            Belum ada proyek. Buat satu untuk mulai membuat API key.
          </Empty>
        </Card>
      ) : (
        <div className="bl-projects">
          {projects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              week={week ? (week[project.id] ?? []) : null}
              keys={keys ? keys.filter((k) => k.project_id === project.id && k.status === 'ACTIVE') : null}
              models={bySlug}
              onEdit={() => setEditing(project)}
            />
          ))}
        </div>
      )}

      {editing && (
        <ProjectModal
          project={editing === 'new' ? null : editing}
          models={catalog ?? []}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}
    </>
  );
}

function ProjectCard({
  project,
  week,
  keys,
  models,
  onEdit,
}: {
  project: Project;
  week: number[] | null;
  keys: ApiKeyRow[] | null;
  models: Map<string, CatalogModel>;
  onEdit: () => void;
}) {
  const today = week ? (week.length > 0 ? week[week.length - 1] : 0) : null;
  const weekTotal = week ? week.reduce((sum, v) => sum + v, 0) : null;
  const keyCount = keys ? keys.length : project.active_keys;

  return (
    <Card className="bl-project">
      <div className="bl-project-head">
        <div className="row" style={{ gap: 12, minWidth: 0, flexWrap: 'nowrap' }}>
          <span className="bl-project-icon">
            <Icon name="folder" size={18} />
          </span>
          <div className="bl-stack" style={{ gap: 3 }}>
            <div className="row wrap" style={{ gap: 8 }}>
              <Link to={`/projects/${project.id}`} className="bl-project-name">
                {project.name}
              </Link>
              <EnvBadge env={project.environment} />
              {project.status !== 'ACTIVE' && <Badge status={project.status}>{STATUS_LABELS[project.status] ?? project.status}</Badge>}
            </div>
            <span className="bl-project-meta">
              {num(keyCount)} key aktif
              {project.created_at && <> · dibuat {relative(project.created_at)}</>}
            </span>
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <Link className="btn btn-sm" to={`/projects/${project.id}`}>
            <Icon name="bars" size={15} />
            Lihat statistik
          </Link>
          <button className="btn btn-sm" onClick={onEdit}>
            <Icon name="gear" size={15} />
            Ubah
          </button>
        </div>
      </div>

      <div className="bl-project-body">
        <BudgetBlock title="Harian" limitLabel="Batas harian" spentLabel="Terpakai hari ini" spent={today} limit={project.daily_budget_idr} tip={DAILY_TIP} />
        <BudgetBlock
          title="Bulanan"
          limitLabel="Batas bulanan"
          spentLabel="Terpakai bulan ini"
          spent={project.month_spend_idr}
          limit={project.monthly_budget_idr}
          tip={MONTHLY_TIP}
        />
        <div className="bl-budget">
          <div className="bl-budget-title">7 hari terakhir</div>
          <dl className="bl-rows">
            <div>
              <dt>Total biaya</dt>
              <dd className="strong">{weekTotal === null ? '—' : idr(weekTotal)}</dd>
            </div>
          </dl>
          {week && week.length > 1 ? (
            <div className="bl-spark" aria-hidden>
              <Sparkline values={week} height={30} />
            </div>
          ) : (
            <div className="bl-spark" />
          )}
          <div className="bl-budget-sub">{weekTotal === null ? 'Data belum tersedia' : `Rata-rata ${idr(weekTotal / 7)}/hari`}</div>
        </div>
      </div>

      <div className="bl-project-keys">
        <div className="bl-section-head">
          <span className="bl-section-label">API key di proyek ini</span>
          <Link to="/api-keys" className="small">
            Kelola di API Key
          </Link>
        </div>
        {keys === null ? (
          <span className="muted small">{num(project.active_keys)} key aktif.</span>
        ) : keys.length === 0 ? (
          <div className="bl-keys-empty">
            <span className="muted">Belum ada key aktif. Aplikasi butuh key untuk memanggil API.</span>
            <Link className="btn btn-sm btn-primary" to={`/api-keys?baru=1&proyek=${project.id}`}>
              <Icon name="plus" size={14} />
              Buat key
            </Link>
          </div>
        ) : (
          <ul className="bl-keylist">
            {keys.slice(0, 4).map((key) => (
              <li key={key.id}>
                <Icon name="key" size={15} className="bl-keylist-icon" />
                <span className="bl-keylist-name truncate">{key.name}</span>
                <span className="bl-id dim">
                  {key.key_prefix}
                  <span className="bl-mask">••••</span>
                </span>
                <span className="grow" />
                <ModelAccessSummary allowed={key.allowed_models} models={models} />
                <span className="bl-keylist-used">{key.last_used_at ? `dipakai ${relative(key.last_used_at)}` : 'belum dipakai'}</span>
              </li>
            ))}
            {keys.length > 4 && (
              <li className="muted small">
                <Link to="/api-keys">+{keys.length - 4} key lainnya</Link>
              </li>
            )}
          </ul>
        )}
      </div>

      <div className="bl-fallback">
        <span className="bl-fallback-label label-tip">
          Model cadangan
          <InfoTip label="Tentang model cadangan">{FALLBACK_TIP}</InfoTip>
        </span>
        {project.fallback_policy.length === 0 ? (
          <>
            <span className="muted grow">Belum diatur. Bila provider gagal, error dikembalikan apa adanya.</span>
            <button className="btn btn-sm btn-ghost" onClick={onEdit}>
              Atur
            </button>
          </>
        ) : (
          <div className="grow" style={{ minWidth: 0 }}>
            <FallbackChain slugs={project.fallback_policy} models={models} />
          </div>
        )}
      </div>
    </Card>
  );
}
